import * as cryptoApi from "app/api/cryptoApi";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { getRoutePathForView } from "app/helpers/routerPathHelper";
import { UserType } from "app/types/userType";
import { TransactionDetailsStatus } from "app/utils/const";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import CryptoFundingRequestQuote from "../CryptoFundingRequestQuote/CryptoFundingRequestQuote";
import CryptoFundingCompleteWithdraw from "./components/CryptoFundingCompleteWithdraw/CryptoFundingCompleteWithdraw";
import "./CryptoFundingWithdraw.scss";

function CryptoFundingWithdraw(props: {
  user: UserType;
  setCryptoFirstTimeWithdrawal: Function;
  cryptoFirstTimeWithdrawal: boolean;
}) {
  const { user, setCryptoFirstTimeWithdrawal, cryptoFirstTimeWithdrawal } =
    props;
  const history = useHistory();
  let [intervalId, setIntervalId] = useState<any>();
  const [address, setAddress] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState("");
  const [amountSending, setAmountSending] = useState(0);
  const [receiveQuantity, setReceiveQuantity] = useState(0);
  const [quoteId, setQuoteId] = useState("");
  const [expireTime, setExpireTime] = useState(null);
  const [status, setStatus] = useState<any>(null);
  const [txHash, setTxHash] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [quoteError, setQuoteError] = useState("");
  const [withdrawError, setWithdrawError] = useState("");
  const prevSelectedCoin = usePreviousSelectedCoin(selectedCoin);
  const [withdrawDetails, setWithdrawDetails] = useState({
    amountSending: amountSending,
    receiveQuantity: receiveQuantity,
    quoteId: quoteId,
    expireTime: expireTime,
    status: status,
    txHash: txHash,
    transactionId: transactionId,
    selectedCoin: selectedCoin,
  });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setPageLoaded(true);
  }, []);

  const checkLocalStorage = () => {
    const localStorageData = JSON.parse(
      localStorage.getItem(`3et-crypto-withdraw-details-${user["userId"]}`)
    );

    if (localStorageData !== null) {
      setAmountSending(localStorageData.amountSending);
      setReceiveQuantity(localStorageData.receiveQuantity);
      setExpireTime(localStorageData.expireTime);
      setStatus(localStorageData.status);
      setTxHash(localStorageData.txHash);
      setTransactionId(localStorageData.transactionId);
      setSelectedCoin(localStorageData.selectedCoin);
      setIsProcessing(localStorageData.isProcessing);
    }
  };

  useEffect(() => {
    // check for withdrawal Details
    checkLocalStorage();
    //
    if (transactionId) pollWithdrawStatus();
    // clean up
    return () => {
      if (status === "SUCCESS" || status === "REJECT") {
        cancelWithdraw();
      } else {
        clearInterval(intervalId);
      }
      setCryptoFirstTimeWithdrawal(false);
    };
  }, []);

  useEffect(() => {
    if (transactionId) pollWithdrawStatus();
  }, [transactionId]);

  // custom hook for getting previous selected coin prop
  function usePreviousSelectedCoin(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  // if selected coin changes update WithdrawDetails Object
  useEffect(() => {
    if (selectedCoin !== "") {
      if (prevSelectedCoin !== selectedCoin) {
        setWithdrawDetails({
          amountSending: amountSending,
          receiveQuantity: receiveQuantity,
          quoteId: quoteId,
          expireTime: expireTime,
          status: status,
          txHash: txHash,
          transactionId: transactionId,
          selectedCoin: selectedCoin,
        });
      }
    }
  }, [selectedCoin]);

  // Save details to localStorage as processing can take a while to complete
  // and we still need to show the status if the user closes and reopens tab
  const saveWithdrawDetails = () => {
    localStorage.setItem(
      `3et-crypto-withdraw-details-${user["userId"]}`,
      JSON.stringify({
        amountSending: amountSending,
        receiveQuantity: receiveQuantity,
        quoteId: quoteId,
        expireTime: expireTime,
        status: status,
        txHash: txHash,
        transactionId: transactionId,
        selectedCoin: selectedCoin,
        isProcessing: isProcessing,
      })
    );
    setWithdrawDetails({
      amountSending: amountSending,
      receiveQuantity: receiveQuantity,
      quoteId: quoteId,
      expireTime: expireTime,
      status: status,
      txHash: txHash,
      transactionId: transactionId,
      selectedCoin: selectedCoin,
      isProcessing: isProcessing,
    });
  };
  // re-write
  const pollWithdrawStatus = () => {
    getTransactionStatus();

    const transactionPollID = setInterval(() => {
      getTransactionStatus();
    }, 5000);
    setIntervalId(transactionPollID);
  };

  const getTransactionStatus = () => {
    if (transactionId) {
      cryptoApi.pollStatus(transactionId).then(resp => {
        setStatus(resp.data.status);
        setTxHash(resp.data.txHash);
        setAmountSending(resp.data.amount || amountSending);
        if (resp.data.status === "SUCCESS" || resp.data.status === "REJECT") {
          setIsProcessing(false);
        }
      });
    }
  };

  const onSelectCoin = item => {
    setSelectedCoin(item.value);
  };

  const onChangeAmount = (id: string, value: number) => {
    setAmountSending(value);
  };

  const onChangeAddress = event => {
    if (event) setAddress(event.target.value);
  };

  const requestQuote = () => {
    setQuoteLoading(true);
    setQuoteError("");

    if (!selectedCoin || !user["currencyCode"] || !amountSending) {
      return;
    }
    cryptoApi
      .requestWithdrawQuote(selectedCoin, user["currencyCode"], amountSending)
      .then(resp => {
        setQuoteLoading(false);
        setReceiveQuantity(resp.data.receiveQuantity);
        setQuoteId(resp.data.quoteId);
        setExpireTime(resp.data.expireTime);
      })
      // .then(() => saveWithdrawDetails())
      .catch(error => {
        console.log("requestWithdrawQuote error: ", error);
        let errorMessage = "";
        if (error.response) {
          if (
            error.response.data.errors.length > 0 &&
            error.response.data.errors[0].messages
          ) {
            errorMessage = error.response.data.errors[0].messages[0];
          } else if (
            error.response.data.errors.length > 0 &&
            error.response.data.errors[0].detail
          ) {
            errorMessage = error.response.data.errors[0].detail;
          }
        }

        setQuoteLoading(false);
        setQuoteError(errorMessage);
      });
  };

  useEffect(() => {
    if (pageLoaded && quoteId) {
      if (withdrawDetails.quoteId !== quoteId) {
        if (quoteId !== "") {
          saveWithdrawDetails();
        }
      }
    }
  }, [quoteId]);

  useEffect(() => {
    if (pageLoaded && txHash) {
      if (withdrawDetails.txHash !== txHash) {
        if (txHash !== "") {
          saveWithdrawDetails();
        }
      }
    }
  }, [pageLoaded, saveWithdrawDetails, status, txHash, withdrawDetails.txHash]);

  const requestWithdraw = () => {
    if (receiveQuantity) {
      setFormLoading(true);
      setWithdrawError("");
      cryptoApi
        .automaticWithdrawal(
          address,
          selectedCoin,
          user["currencyCode"],
          receiveQuantity,
          quoteId
        )
        .then(resp => {
          console.log("requestWithdraw resp: ", resp);
          setStatus("NEW");
          setIsProcessing(true);
          setFormLoading(false);
          // add check for first time withdrawal
          if (
            resp.data.adminApproval ===
            TransactionDetailsStatus.PENDING_APPROVAL
          ) {
            setCryptoFirstTimeWithdrawal(true);
          } else {
            setTransactionId(resp.data.transactionId);
            setCryptoFirstTimeWithdrawal(false);
          }
        })
        .then(() => {
          pollWithdrawStatus();
          saveWithdrawDetails();
        })
        .catch(error => {
          console.log("error: ", error);
          setIsProcessing(false);
          let errorMessage = "";
          if (error.response) {
            if (
              error.response.data.errors.length > 0 &&
              error.response.data.errors[0].messages
            ) {
              errorMessage = error.response.data.errors[0].messages[0];
            } else if (
              error.response.data.errors.length > 0 &&
              error.response.data.errors[0].detail
            ) {
              errorMessage = error.response.data.errors[0].detail;
            }
          }
          setFormLoading(false);
          setWithdrawError(errorMessage);
          throw error;
        });
    }
  };

  const cancelWithdraw = () => {
    setReceiveQuantity(0);
    setQuoteId("");
    setExpireTime(null);
    setStatus(null);
    setTxHash(null);
    setTransactionId(null);
    setSelectedCoin("");
    setAmountSending(0);
    localStorage.removeItem(`3et-crypto-withdraw-details-${user["userId"]}`);
    setWithdrawDetails({
      amountSending: 0,
      receiveQuantity: 0,
      quoteId: "",
      expireTime: null,
      status: null,
      txHash: null,
      transactionId: null,
      selectedCoin: "",
    });
    setQuoteError("");
    if (intervalId) {
      intervalId = clearInterval(intervalId);
    }
  };

  const backToHomepage = () => {
    const path = getRoutePathForView();
    history.push(path);
    // history.push("/manageFunding");
  };

  const copyAmountInfo = () => {
    if (receiveQuantity) {
      navigator.clipboard.writeText(receiveQuantity.toString()).then(() => {
        console.log("Amount added to clipboard: " + receiveQuantity);
      });
    }
  };

  if (formLoading) return <LoadingSpinner />;

  return (
    <div className="crypto-funding-withdraw">
      {cryptoFirstTimeWithdrawal && (
        <div className="first-time-withdrawal">
          <FormattedMessage
            tagName="span"
            id="cashier-first-withdrawal-message-crypto"
          />
        </div>
      )}
      {!cryptoFirstTimeWithdrawal && (
        <div className="crypto-funding-withdraw__content">
          {!withdrawDetails.receiveQuantity ? (
            <CryptoFundingRequestQuote
              type="WITHDRAW"
              onSelectCoin={onSelectCoin}
              onChangeAmount={onChangeAmount}
              requestQuote={requestQuote}
              selectedCoin={withdrawDetails.selectedCoin}
              user={user}
              quoteError={quoteError}
              quoteLoading={quoteLoading}
              amountSending={amountSending}
            />
          ) : (
            <CryptoFundingCompleteWithdraw
              withdrawDetails={withdrawDetails}
              requestQuote={requestQuote}
              requestWithdraw={requestWithdraw}
              cancelWithdraw={cancelWithdraw}
              onChangeAddress={onChangeAddress}
              user={user}
              currency={withdrawDetails.selectedCoin}
              withdrawError={withdrawError}
              backToHomepage={backToHomepage}
              quoteLoading={quoteLoading}
              copyAmountInfo={copyAmountInfo}
              isProcessing={isProcessing}
              status={status}
            />
          )}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  cryptoFirstTimeWithdrawal: state.cashierModel.cryptoFirstTimeWithdrawal,
});

const mapDispatchToProps = (dispatch: any) => ({
  setCryptoFirstTimeWithdrawal: (data: boolean) => {
    dispatch.cashierModel.setCryptoFirstTimeWithdrawal(data);
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(CryptoFundingWithdraw));
